import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronDown, CalendarDays, CheckSquare, MoreVertical,
  Video, VideoOff, CheckCircle2, XCircle, Mail, Phone, X, MessageSquare,
  Trash2, ArrowRightLeft, CalendarClock, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileBottomNav, C, MobileSheet} from './MobileShell';
import { formatClocks, formatTime } from '@/lib/utils';
import { isStaffArea, AREA_LABEL } from './areas';
import { todayIsoLocal, mondayOfWeek, addDaysIso } from './homeMenu';
import {
  contactsForLesson, parseHm, isoToCourseDow,
  endFromStart, lessonDurationMinutes, messageTargets,
} from './lessonContacts';
import { splitPhoneNumbers, toTelHref } from '@/components/PhoneLink';
import AreaSwitchControl from './AreaSwitchControl';
import SchoolSwitchControl from './SchoolSwitchControl';
import {
  fetchAttendanceCategories, cancellationCategories, presenceCategories, consequenceText,
} from './attendanceCategories';
import { relocationLines, lessonStatusSuffix } from './relocationHint';
import { retryOnCollision } from '@/components/schedule/collisionWarning';

import { resolveAttendanceStatus } from '@/lib/attendanceStatus';

const DAY_LABELS = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'];
/** Fallback, solange die Kategorien des Mandanten noch nicht geladen sind. */
const ATT_OPTIONS = [
  { status: 'present', label: 'Anwesend / erteilt' },
  { status: 'absent_excused', label: 'Abwesend (entschuldigt)' },
  { status: 'absent_unexcused', label: 'Abwesend (unentschuldigt)' },
  { status: 'none', label: 'Eintrag löschen' },
];

function weekDates(mondayIso) {
  return DAY_LABELS.map((_, i) => addDaysIso(mondayIso, i));
}

function weekdayLong(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'long' });
}

function dateShort(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
}

function birthLine(student) {
  const iso = student?.date_of_birth;
  if (!iso || String(iso).length < 10) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return '';
  const dateText = `${d}.${m}.${y}`;
  return student.age_years == null || student.age_years === ''
    ? dateText
    : `${dateText} · ${student.age_years} Jahre`;
}

function lessonSections(list, grouped) {
  if (!grouped) return [{ key: 'day', heading: null, date: null, entries: list }];
  const sections = [];
  for (const entry of list) {
    const last = sections[sections.length - 1];
    if (!last || last.date !== entry.date) {
      sections.push({
        key: entry.date,
        date: entry.date,
        heading: `${dateShort(entry.date)} ${weekdayLong(entry.date)}`,
        entries: [entry],
      });
    } else {
      last.entries.push(entry);
    }
  }
  return sections;
}

function studentActiveKey(entry, studentId) {
  return `${entry.course_id}|${entry.date}|${studentId || ''}`;
}

function studentLine(e) {
  if (e.student_label) return e.student_label;
  const list = (e.students || []).map((s) => s.name).filter(Boolean);
  return list.join(', ');
}

function isGroupLesson(e) {
  return e?.course_type === 'group' || (e?.students || []).length > 1;
}

const GROUP_STATUS_LABEL = {
  erteilt: 'Erteilt',
  erteilt_online: 'Erteilt',
  ea: 'EA',
  ua: 'UA',
  la: 'LA',
  offen: 'Offen',
  verlegt: 'Verlegt',
};
const GROUP_STATUS_ORDER = ['Erteilt', 'EA', 'UA', 'LA', 'Offen', 'Verlegt'];

function groupAttendanceSummary(students) {
  const list = students || [];
  const counts = new Map();
  for (const student of list) {
    const resolved = resolveAttendanceStatus({
      category_code: student.category_code,
      status: student.status,
      erteilt: student.erteilt,
    });
    if (!resolved) continue;
    const label = GROUP_STATUS_LABEL[resolved.code] || resolved.short;
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  if (!counts.size) {
    return list.length === 1 ? '1 Teilnehmer' : `${list.length} Teilnehmer`;
  }
  const parts = GROUP_STATUS_ORDER
    .filter((label) => counts.has(label))
    .map((label) => `${counts.get(label)} ${label}`);
  return `${list.length} Teilnehmer · ${parts.join(' · ')}`;
}

function roomLine(e) {
  return [e.location_name, e.room_name].filter(Boolean).join(' · ')
    || (e.is_online ? 'Online' : '');
}

const UNTERRICHT_VIEW_KEY = 'mamv-unterricht-view';
/** Sitzt auf der Tab-Leiste. Portal an den Body, sonst klebt die Leiste am Listenende. */
const VIEW_DOCK_BOTTOM = 'calc(3.75rem + env(safe-area-inset-bottom, 0px))';

function UnterrichtViewDock({ view, onChange }) {
  return createPortal(
    <div
      className="fixed inset-x-0 z-20 flex justify-center"
      style={{ bottom: VIEW_DOCK_BOTTOM }}
      data-testid="mobile-unterricht-tabs"
    >
      <div
        className="flex w-full max-w-md border-t shadow-[0_-6px_16px_rgba(23,32,51,0.12)]"
        style={{ background: C.card, borderColor: C.border }}
      >
        <button type="button" onClick={() => onChange('list')}
                className="flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold"
                style={{ color: view === 'list' ? C.red : C.dim }}>
          <CheckSquare className="w-5 h-5" />
          Anwesenheitsliste
        </button>
        <button type="button" onClick={() => onChange('calendar')}
                className="flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold"
                style={{ color: view === 'calendar' ? C.red : C.dim }}>
          <CalendarDays className="w-5 h-5" />
          Kalenderansicht
        </button>
      </div>
    </div>,
    document.body,
  );
}

function readUnterrichtView() {
  try {
    const raw = sessionStorage.getItem(UNTERRICHT_VIEW_KEY);
    const data = raw ? JSON.parse(raw) : null;
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

function savedIso(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? String(value) : '';
}

function lessonCircleSource(entry) {
  if (entry?.relocated_to_date) return entry;
  const students = entry?.students || [];
  if (students.length !== 1) return entry;
  const student = students[0];
  if (!student?.category_code && !student?.status && !student?.erteilt) return entry;
  return {
    category_code: student.category_code,
    status: student.status,
    erteilt: student.erteilt,
    lesson_date: entry.date,
    makeup_open: entry.makeup_open,
  };
}

function lessonCircleFields(students, code) {
  const chosen = code || (() => {
    const codes = [...new Set((students || []).map((s) => s.category_code).filter((c) => c && c !== 'offen'))];
    if (codes.length === 1) return codes[0];
    if (codes.length > 1) return 'offen';
    return null;
  })();
  if (!chosen || chosen === 'offen') {
    return {
      status_code: null,
      category_code: null,
      cancel_category_code: null,
      cancelled: false,
      erteilt: false,
      makeup_open: false,
    };
  }
  const absent = chosen === 'ea' || chosen === 'ua' || chosen === 'la';
  return {
    status_code: chosen,
    category_code: chosen,
    cancel_category_code: absent ? chosen : null,
    cancelled: absent,
    erteilt: chosen === 'erteilt' || chosen === 'erteilt_online',
  };
}

function tapModeLabel(opt) {
  if (opt.code === 'erteilt') return 'Erteilt';
  if (opt.code === 'erteilt_online') return 'Online';
  return opt.short_label || opt.label;
}

function MoveHeader({ entry, title }) {
  const group = isGroupLesson(entry);
  const place = [entry.location_name, entry.room_name ? `Raum ${entry.room_name}` : ''].filter(Boolean).join(' · ');
  return (
    <div data-testid="mobile-unterricht-move-context">
      <p className="text-sm font-bold">{title}</p>
      {!group && (
        <p className="text-xs" style={{ color: C.dim }}>Schüler: {studentLine(entry) || '—'}</p>
      )}
      <p className="text-xs" style={{ color: C.dim }}>Unterricht: {entry.name || '—'}</p>
      <p className="text-xs" style={{ color: C.dim }}>
        Bisheriger Termin: {weekdayLong(entry.date)}, {formatTime(entry.start)}–{formatTime(entry.end)} Uhr
      </p>
      {place ? <p className="text-xs" style={{ color: C.dim }}>Ort: {place}</p> : null}
    </div>
  );
}

function PlaceFields({ form, setForm, places, rooms, idPrefix }) {
  const roomList = Array.isArray(rooms) ? rooms : [];
  const list = roomList.filter((room) => (
    !form.location_id || String(room.location_id) === String(form.location_id)
  ));
  return (
    <>
      <label className="block text-xs font-semibold" style={{ color: C.dim }}>
        Ort
        <select
          value={form.location_id || ''}
          data-testid={`${idPrefix}-place`}
          className="mt-1 w-full rounded-lg px-3 py-2"
          style={{ border: `1px solid ${C.border}` }}
          onChange={(e) => {
            const locationId = e.target.value;
            setForm((current) => ({
              ...current,
              location_id: locationId,
              room_id: roomList.some((room) => (
                String(room.id) === String(current.room_id) && String(room.location_id) === String(locationId)
              )) ? current.room_id : '',
            }));
          }}
        >
          <option value="">Ort wählen</option>
          {(Array.isArray(places) ? places : []).map((place) => (
            <option key={place.id} value={place.id}>{place.name}</option>
          ))}
        </select>
      </label>
      <label className="block text-xs font-semibold" style={{ color: C.dim }}>
        Raum
        <select
          value={form.room_id || ''}
          data-testid={`${idPrefix}-room`}
          className="mt-1 w-full rounded-lg px-3 py-2"
          style={{ border: `1px solid ${C.border}` }}
          onChange={(e) => setForm((current) => ({ ...current, room_id: e.target.value }))}
        >
          <option value="">Raum wählen</option>
          {list.map((room) => (
            <option key={room.id} value={room.id}>{room.name}</option>
          ))}
        </select>
      </label>
    </>
  );
}

export default function MobileMyLessons() {
  const nav = useNavigate();
  const { me, loading, area, switchArea, teacherId: activeTeacher } = useMobile();
  const [monday, setMonday] = useState(() => savedIso(readUnterrichtView().monday) || mondayOfWeek(todayIsoLocal()));
  const [selectedDay, setSelectedDay] = useState(() => savedIso(readUnterrichtView().selectedDay) || todayIsoLocal());
  const [showAll, setShowAll] = useState(() => !!readUnterrichtView().showAll);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('list'); // list | calendar
  const [actionEntry, setActionEntry] = useState(null);
  const [menuEntry, setMenuEntry] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [notePicker, setNotePicker] = useState(null);
  const [contactEntry, setContactEntry] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);
  const [attEntry, setAttEntry] = useState(null);
  const [moveOnce, setMoveOnce] = useState(null);
  const [moveSeries, setMoveSeries] = useState(null);
  const [moveForm, setMoveForm] = useState({ date: '', start: '', end: '', location_id: '', room_id: '' });
  const [seriesForm, setSeriesForm] = useState({ dow: 0, start: '', date: '', location_id: '', room_id: '' });
  const [messageEntry, setMessageEntry] = useState(null);
  const [messageAudience, setMessageAudience] = useState('both');
  const [messageDelivery, setMessageDelivery] = useState('group');
  const [places, setPlaces] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [areaOpen, setAreaOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cancelEntry, setCancelEntry] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [tapModes, setTapModes] = useState({});
  const [activeStudentKey, setActiveStudentKey] = useState(null);

  const staff = isStaffArea(area);
  const teacherId = activeTeacher || me?.teacher_id || null;

  useEffect(() => {
    sessionStorage.setItem(UNTERRICHT_VIEW_KEY, JSON.stringify({
      monday, selectedDay, showAll: !!showAll,
    }));
  }, [monday, selectedDay, showAll]);
  const days = useMemo(() => weekDates(monday), [monday]);
  const areas = me?.areas || [];

  useEffect(() => {
    if (!me) return;
    mapi.get('/messages/unread-count').then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    mapi.get('/app/contacts').then((r) => setContacts(r.data || [])).catch(() => {});
    fetchAttendanceCategories().then(setCategories);
  }, [me]);

  useEffect(() => {
    if (!moveOnce && !moveSeries) return undefined;
    let cancelled = false;
    mapi.get('/locations').then((r) => { if (!cancelled) setPlaces(r.data || []); }).catch(() => {});
    mapi.get('/locations/rooms').then((r) => { if (!cancelled) setRooms(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [moveOnce, moveSeries]);

  const reloadWeek = () => {
    if (!me || !staff || !teacherId) return;
    const end = addDaysIso(monday, 6);
    setBusy(true);
    mapi.get('/lessons/plan-range', {
      params: {
        start_date: monday,
        end_date: end,
        include_students: true,
        teacher_id: teacherId,
      },
    })
      .then((r) => {
        const list = (r.data?.entries || [])
          .filter((e) => String(e.teacher_id || '') === String(teacherId))
          .sort((a, b) => `${a.date}${a.start}`.localeCompare(`${b.date}${b.start}`));
        setEntries(list);
      })
      .catch(() => {
        setEntries([]);
        toast.error('Stundenplan konnte nicht geladen werden');
      })
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (!me || !staff) return;
    if (!teacherId) {
      setEntries([]);
      return;
    }
    reloadWeek();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, staff, teacherId, monday]);

  const visible = useMemo(() => {
    if (showAll) return entries;
    return entries.filter((e) => e.date === selectedDay);
  }, [entries, selectedDay, showAll]);
  const cancelOptions = useMemo(() => cancellationCategories(categories), [categories]);
  const presenceOptions = useMemo(() => presenceCategories(categories), [categories]);
  const tapOptions = useMemo(
    () => presenceOptions.filter((opt) => opt.code && opt.code !== 'la'),
    [presenceOptions],
  );

  const groupKey = (e) => `${e.course_id}|${e.date}`;
  const toggleGroup = (e) => {
    const key = groupKey(e);
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const expandGroup = (e) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey(e)]: true }));
    setActionEntry(null);
  };

  const dayLabel = showAll
    ? `Woche ab ${dateShort(monday)}`
    : `${dateShort(selectedDay)} ${weekdayLong(selectedDay).toUpperCase()}`;

  if (loading) {
    return (
      <MobileShell bottomPad={false}>
        <div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div>
      </MobileShell>
    );
  }
  if (!me) return <Navigate to="/m/login" replace />;
  if (!staff) return <Navigate to="/m" replace />;

  const shiftWeek = (delta) => {
    const next = addDaysIso(monday, delta * 7);
    setMonday(next);
    if (!showAll) {
      const idx = days.indexOf(selectedDay);
      setSelectedDay(addDaysIso(next, idx >= 0 ? idx : 0));
    }
  };

  const openMoveOnce = (entry) => {
    const start = String(entry.start || '').slice(0, 5);
    setMoveForm({
      date: entry.date,
      start,
      end: endFromStart(start, lessonDurationMinutes(entry)),
      location_id: entry.location_id || '',
      room_id: entry.room_id || '',
    });
    setMoveOnce(entry);
  };

  const openMoveSeries = (entry) => {
    setSeriesForm({
      dow: isoToCourseDow(entry.date),
      start: String(entry.start || '').slice(0, 5),
      date: entry.date,
      location_id: entry.location_id || '',
      room_id: entry.room_id || '',
    });
    setMoveSeries(entry);
  };

  const sendLessonMessage = async () => {
    const entry = messageEntry;
    if (!entry) return;
    const targets = messageTargets(contacts, entry, messageAudience);
    if (!targets.length) {
      toast.info('Niemand mit App-Konto in dieser Auswahl');
      return;
    }
    const audience = messageAudience === 'students' || messageAudience === 'guardians'
      ? messageAudience : 'both';
    try {
      if (!entry.personOnly) {
        const r = await mapi.post('/messages/conversations/from-course', {
          course_id: entry.course_id,
          audience,
          delivery: messageDelivery,
        });
        setMessageEntry(null);
        if (messageDelivery === 'bcc') {
          toast.success('Einzelchats angelegt — Antworten gehen nur an Sie');
          nav('/m/messages');
        } else {
          nav(`/m/chat/${r.data.id}`);
        }
        return;
      }
      if (messageDelivery === 'bcc' || targets.length === 1) {
        const created = [];
        for (const person of targets) {
          const r = await mapi.post('/messages/conversations', {
            kind: 'direct',
            participant_ids: [person.user_id],
          });
          created.push(r.data.id);
        }
        setMessageEntry(null);
        if (created.length === 1) nav(`/m/chat/${created[0]}`);
        else {
          toast.success('Einzelchats angelegt — Antworten gehen nur an Sie');
          nav('/m/messages');
        }
        return;
      }
      const r = await mapi.post('/messages/conversations', {
        kind: 'group',
        participant_ids: targets.map((person) => person.user_id),
        title: entry.name || targets.map((person) => person.name).filter(Boolean).join(', '),
      });
      setMessageEntry(null);
      nav(`/m/chat/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Nachricht konnte nicht gestartet werden');
    }
  };

  const startChat = async (c) => {
    if (!c.user_id) {
      toast.info(`${formatClocks(c.name) || c.name} ist noch nicht in der App registriert`);
      return;
    }
    try {
      const r = await mapi.post('/messages/conversations', { kind: 'direct', participant_ids: [c.user_id] });
      setContactEntry(null);
      setContactDetail(null);
      nav(`/m/chat/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Chat konnte nicht gestartet werden');
    }
  };

  const ensureLessonId = async (entry) => {
    if (entry.lesson_id) return entry.lesson_id;
    const start = parseHm(entry.start);
    const end = parseHm(entry.end);
    if (!start || !end) throw new Error('Uhrzeit fehlt');
    const r = await mapi.post('/lessons/', {
      course_id: entry.course_id,
      date: entry.date,
      start_time: start,
      end_time: end,
      room_id: entry.room_id || null,
      teacher_id: teacherId,
      status: 'scheduled',
    });
    return r.data.id;
  };

  const guardFinalized = (entry) => {
    if (entry?.is_finalized) {
      toast.error('Finalisiert — nicht mehr per Tipp änderbar.');
      return true;
    }
    return false;
  };

  const finalizeEntry = async (entry) => {
    if (!entry?.is_captured || entry.is_finalized) return;
    try {
      const r = await mapi.post(`/attendance-lists/courses/${entry.course_id}/finalize-date`, {
        date: entry.date,
      });
      const n = r.data?.finalized || 0;
      toast.success(n ? 'Termin finalisiert' : 'Keine erfassten Einträge zum Finalisieren');
      setActionEntry(null);
      reloadWeek();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Finalisieren fehlgeschlagen');
    }
  };

  const finalizeVisible = async () => {
    if (!teacherId) return;
    const dates = showAll
      ? [...new Set(visible.map((e) => e.date))]
      : [selectedDay];
    if (!window.confirm('Erfasste Einträge finalisieren? Offene Termine bleiben offen.')) return;
    try {
      let total = 0;
      for (const d of dates) {
        const r = await mapi.post(`/attendance-lists/teachers/${teacherId}/finalize-day`, { date: d });
        total += r.data?.finalized || 0;
      }
      toast.success(total ? `${total} Einträge finalisiert` : 'Keine erfassten Einträge zum Finalisieren');
      reloadWeek();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Finalisieren fehlgeschlagen');
    }
  };

  const runOnline = async (entry, isOnline) => {
    try {
      await mapi.put(`/online-lessons/courses/${entry.course_id}/dates/${entry.date}`, {
        is_online: isOnline,
      });
      setEntries((prev) => prev.map((e) => (
        e.course_id === entry.course_id && e.date === entry.date
          ? { ...e, is_online: !!isOnline }
          : e
      )));
      toast.success(isOnline ? 'Online vorgemerkt' : 'Online entfernt');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setActionEntry(null);
    }
  };

  const applyDirect = (entry, code) => {
    const known = [...cancelOptions, ...presenceOptions].find((c) => c.code === code);
    const fallback = {
      ea: { code: 'ea', label: 'EA', teacher_status: 'absent_excused' },
      ua: { code: 'ua', label: 'UA', teacher_status: 'absent_unexcused' },
      la: { code: 'la', label: 'LA' },
    }[code];
    setActionEntry(null);
    applyCancelCategory(entry, known || fallback);
  };

  const beginNote = (entry, student) => {
    if (!student?.id) {
      toast.error('Keine Schüler:in für die Bemerkung.');
      return;
    }
    setMenuEntry(null);
    setNotePicker(null);
    setNoteTarget({ courseId: entry.course_id, date: entry.date, student });
    setNoteDraft(student.internal_note || '');
  };

  const openNote = (entry) => {
    const students = entry.students || [];
    if (students.length > 1) {
      setMenuEntry(null);
      setNotePicker(entry);
      return;
    }
    beginNote(entry, students[0]);
  };

  const saveNote = async () => {
    const student = noteTarget?.student;
    if (!student?.id) return;
    const note = noteDraft.trim();
    try {
      await mapi.put(`/teacher/students/${student.id}/internal-note`, { note });
      setEntries((prev) => prev.map((row) => ({
        ...row,
        students: (row.students || []).map((item) => (
          String(item.id) === String(student.id)
            ? { ...item, internal_note: note || null }
            : item
        )),
      })));
      setNoteTarget(null);
      toast.success(note ? 'Bemerkung gespeichert' : 'Bemerkung gelöscht');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bemerkung konnte nicht gespeichert werden');
    }
  };

  /** „Ausfall“: Kategorie wählen lassen — Folgen hängen an der Kategorie. */
  const runCancel = (entry) => {
    setActionEntry(null);
    if (!cancelOptions.length) {
      runCancelPlain(entry);
      return;
    }
    setCancelEntry(entry);
  };

  /** Fallback ohne konfigurierte Kategorien: Termin nur als ausgefallen markieren. */
  const runCancelPlain = async (entry) => {
    if (guardFinalized(entry)) return;
    try {
      const lid = await ensureLessonId(entry);
      await mapi.put(`/lessons/${lid}`, { status: 'cancelled' });
      setEntries((prev) => prev.map((e) => (
        e.course_id === entry.course_id && e.date === entry.date
          ? { ...e, lesson_id: lid, cancelled: true }
          : e
      )));
      toast.success('Ausfall eingetragen');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Ausfall fehlgeschlagen');
    }
  };

  /** Ausfallkategorie auf alle Teilnehmer:innen des Termins anwenden. */
  const applyCancelCategory = async (entry, category) => {
    if (guardFinalized(entry)) return;
    const students = entry.students || [];
    if (!students.length) {
      toast.error('Keine Schüler:innen am Termin — Ausfall nicht möglich.');
      return;
    }
    try {
      const results = await Promise.all(students.map((s) => mapi.post(`/attendance-lists/courses/${entry.course_id}/mark`, {
        lesson_date: entry.date,
        student_id: s.id,
        category_code: category.code,
      })));
      const lessonId = results[0]?.data?.lesson_event_id || entry.lesson_id;
      const nextStudents = (entry.students || []).map((student) => ({
        ...student,
        status: category.teacher_status || null,
        category_code: category.code,
        erteilt: !!category.counts_as_taught,
      }));
      const next = {
        ...entry,
        lesson_id: lessonId,
        students: nextStudents,
        ...lessonCircleFields(nextStudents, category.code),
        makeup_open: !!category.requires_makeup,
      };
      setEntries((prev) => prev.map((e) => (
        e.course_id === entry.course_id && e.date === entry.date ? next : e
      )));
      setCancelEntry(null);
      toast.success(`${category.label} — ${consequenceText(category)}`);
      if (category.requires_new_date) {
        openMoveOnce(next);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ausfall fehlgeschlagen');
    }
  };

  const runTaught = async (entry, online = false) => {
    if (guardFinalized(entry)) return;
    const students = entry.students || [];
    if (!students.length) {
      toast.error('Keine Schüler:innen am Termin — Erteilt nicht möglich.');
      return;
    }
    try {
      await Promise.all(students.map((s) => mapi.post(`/attendance-lists/courses/${entry.course_id}/mark`, {
        lesson_date: entry.date,
        student_id: s.id,
        status: 'present',
      })));
      if (online) {
        await mapi.put(`/online-lessons/courses/${entry.course_id}/dates/${entry.date}`, {
          is_online: true,
        });
      }
      const taughtCode = online ? 'erteilt_online' : 'erteilt';
      setEntries((prev) => prev.map((row) => {
        if (row.course_id !== entry.course_id || row.date !== entry.date) return row;
        const nextStudents = (row.students || []).map((student) => ({
          ...student,
          status: 'present',
          category_code: taughtCode,
          erteilt: true,
        }));
        return {
          ...row,
          is_online: online || row.is_online,
          students: nextStudents,
          ...lessonCircleFields(nextStudents, taughtCode),
        };
      }));
      toast.success(online ? 'Erteilt (Online)' : 'Erteilt');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erteilt fehlgeschlagen');
    } finally {
      setActionEntry(null);
    }
  };

  const markAttendance = async (entry, studentId, status, categoryCode) => {
    if (guardFinalized(entry)) return;
    try {
      const r = await mapi.post(`/attendance-lists/courses/${entry.course_id}/mark`, {
        lesson_date: entry.date,
        student_id: studentId,
        ...(categoryCode ? { category_code: categoryCode } : { status }),
      });
      toast.success(status === 'none' ? 'Eintrag gelöscht' : 'Anwesenheit gespeichert');
      setEntries((prev) => prev.map((row) => {
        if (row.course_id !== entry.course_id || row.date !== entry.date) return row;
        const nextStudents = (row.students || []).map((student) => (
          String(student.id) === String(studentId)
            ? {
              ...student,
              status: status === 'none' ? null : (r.data?.status ?? status ?? null),
              category_code: status === 'none' ? null : (r.data?.category_code ?? categoryCode ?? null),
              erteilt: status !== 'none' && (
                r.data?.status === 'present'
                || r.data?.category_code === 'erteilt'
                || r.data?.category_code === 'erteilt_online'
                || categoryCode === 'erteilt'
                || categoryCode === 'erteilt_online'
              ),
            }
            : student
        ));
        return { ...row, students: nextStudents, ...lessonCircleFields(nextStudents) };
      }));
      if (r.data?.requires_new_date) {
        const next = {
          ...entry,
          lesson_id: r.data.lesson_event_id || entry.lesson_id,
          cancelled: true,
          makeup_open: true,
        };
        setAttEntry(null);
        setMoveForm({ date: entry.date, start: entry.start || '', end: entry.end || '' });
        setMoveOnce(next);
      } else {
        setAttEntry(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const clearAttendance = async (entry) => {
    if (guardFinalized(entry)) return;
    const students = entry.students || [];
    if (!students.length) {
      toast.info('Keine Einträge vorhanden');
      return;
    }
    try {
      await Promise.all(students.map((s) => mapi.post(`/attendance-lists/courses/${entry.course_id}/mark`, {
        lesson_date: entry.date,
        student_id: s.id,
        status: 'none',
      })));
      toast.success('Eingaben gelöscht');
      setEntries((prev) => prev.map((row) => {
        if (row.course_id !== entry.course_id || row.date !== entry.date) return row;
        const nextStudents = (row.students || []).map((student) => ({
          ...student,
          status: null,
          category_code: null,
          erteilt: false,
        }));
        return { ...row, students: nextStudents, ...lessonCircleFields(nextStudents) };
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setActionEntry(null);
    }
  };

  const placePayload = (form) => ({
    location_id: form.location_id || null,
    room_id: form.room_id || null,
  });

  const submitMoveOnce = async () => {
    const entry = moveOnce;
    if (!entry) return;
    const start = parseHm(moveForm.start);
    const end = parseHm(moveForm.end);
    if (!moveForm.date || !start || !end) {
      toast.error('Datum und Uhrzeit angeben');
      return;
    }
    try {
      const lid = await ensureLessonId(entry);
      const payload = {
        date: moveForm.date,
        start_time: start,
        end_time: end,
        room_id: moveForm.room_id || null,
      };
      const saved = await retryOnCollision(
        () => mapi.post(`/lessons/${lid}/relocate`, payload),
        () => mapi.post(`/lessons/${lid}/relocate`, { ...payload, allow_collisions: true }),
      );
      if (!saved) return;
      toast.success('Termin verlegt — Ursprung bleibt verbunden');
      setMoveOnce(null);
      setActionEntry(null);
      reloadWeek();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verlegen fehlgeschlagen');
    }
  };

  const submitMoveSeries = async () => {
    const entry = moveSeries;
    if (!entry) return;
    const start = parseHm(seriesForm.start);
    if (seriesForm.dow == null || !start) {
      toast.error('Wochentag und Uhrzeit angeben');
      return;
    }
    try {
      const payload = {
        valid_from: seriesForm.date || entry.date,
        day_of_week: Number(seriesForm.dow),
        start_time: start.slice(0, 5),
        duration_minutes: lessonDurationMinutes(entry),
        ...placePayload(seriesForm),
      };
      const saved = await retryOnCollision(
        () => mapi.post(`/courses/${entry.course_id}/periods`, payload),
        () => mapi.post(`/courses/${entry.course_id}/periods`, { ...payload, allow_collisions: true }),
      );
      if (!saved) return;
      toast.success('Terminserie dauerhaft geändert');
      setMoveSeries(null);
      setActionEntry(null);
      reloadWeek();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Serie ändern fehlgeschlagen');
    }
  };

  const lessonContacts = contactEntry ? contactsForLesson(contacts, contactEntry) : [];

  const calStartHour = (() => {
    const mins = visible.map((e) => {
      const [h, m] = String(e.start || '15:00').split(':').map(Number);
      return h * 60 + m;
    });
    if (!mins.length) return 14;
    return Math.max(7, Math.floor(Math.min(...mins) / 60) - 1);
  })();
  const calEndHour = Math.min(22, calStartHour + 6);
  const pxPerMin = 2.2;

  return (
    <MobileShell
      bottomNav={<MobileBottomNav unread={unread} />}
    >
      <div className="mobile-below-clock-only sticky top-0 z-20" style={{ background: C.header, color: '#fff' }}>
        <div className="flex items-center gap-1 px-2 py-2">
          <button type="button" onClick={() => nav('/m')} className="p-1.5" data-testid="mobile-unterricht-back">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0 text-center">
            <p className="text-sm font-bold">Mein Unterricht</p>
            <SchoolSwitchControl light />
            <p className="text-[10px] opacity-90">Woche ab {dateShort(monday)}</p>
          </div>
          <AreaSwitchControl light onOpenPicker={() => setAreaOpen(true)} />
          <button type="button" onClick={() => shiftWeek(-1)} className="p-1.5" data-testid="mobile-unterricht-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button type="button" onClick={() => { const t = todayIsoLocal(); setMonday(mondayOfWeek(t)); setSelectedDay(t); setShowAll(false); }}
                  className="p-1.5" data-testid="mobile-unterricht-today">
            <CalendarDays className="w-5 h-5" />
          </button>
          <button type="button" onClick={() => shiftWeek(1)} className="p-1.5" data-testid="mobile-unterricht-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="flex gap-1 px-2 pb-2 overflow-x-auto" data-testid="mobile-unterricht-days">
          {days.map((iso, i) => {
            const active = !showAll && iso === selectedDay;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => { setSelectedDay(iso); setShowAll(false); }}
                className="min-w-[2.4rem] rounded-md py-1.5 text-[11px] font-bold"
                style={{
                  background: active ? '#fff' : 'rgba(255,255,255,0.15)',
                  color: active ? C.red : '#fff',
                }}
                data-testid={`mobile-unterricht-day-${DAY_LABELS[i].toLowerCase()}`}
              >
                {DAY_LABELS[i]}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="min-w-[2.8rem] rounded-md py-1.5 text-[11px] font-bold"
            style={{
              background: showAll ? '#fff' : 'rgba(255,255,255,0.15)',
              color: showAll ? C.red : '#fff',
            }}
            data-testid="mobile-unterricht-day-all"
          >
            ALLE
          </button>
        </div>
      </div>

      {!teacherId && (
        <div className="m-4 rounded-xl px-4 py-3 text-sm" style={{ background: C.rose, border: `1px solid ${C.border}` }}
             data-testid="mobile-unterricht-no-teacher">
          Keine Lehrkraft verknüpft. Bitte Kennung freischalten oder die Verwaltung kontaktieren.
        </div>
      )}

      {view === 'list' ? (
        <div className="flex-1 overflow-y-auto px-3 pt-3 space-y-2" data-testid="mobile-unterricht-list"
             style={{ paddingBottom: '4.5rem' }}>
          <p className="text-[11px] font-semibold px-1" style={{ color: C.dim }}>{dayLabel}</p>
          {busy && <p className="text-xs px-1" style={{ color: C.dim }}>Laden…</p>}
          {!busy && visible.length === 0 && (
            <p className="text-sm px-1 py-6 text-center" style={{ color: C.dim }} data-testid="mobile-unterricht-empty">
              Kein Unterricht an diesem Tag.
            </p>
          )}
          {lessonSections(visible, showAll).map((section) => (
            <div key={section.key} className="space-y-2">
            {section.heading ? (
              <p
                className="text-[11px] font-semibold px-1 pt-1"
                style={{ color: C.dim }}
                data-testid={`mobile-unterricht-date-heading-${section.date}`}
              >
                {section.heading}
              </p>
            ) : null}
            {section.entries.map((e, idx) => (
            <div
              key={`${e.course_id}-${e.date}-${e.start}-${idx}`}
              className="rounded-xl px-3 py-2.5 shadow-sm"
              style={{
                background: e.cancelled ? '#F3F4F6' : C.card,
                border: `1px solid ${C.border}`,
                opacity: e.cancelled ? 0.75 : 1,
              }}
              data-testid={`mobile-unterricht-row-${e.course_id}`}
            >
              <div className="flex items-start gap-2" data-testid={`mobile-unterricht-head-${e.course_id}`}>
                {!isGroupLesson(e) && (
                <button
                  type="button"
                  className="flex w-14 items-center justify-center border-r shrink-0 self-start"
                  style={{ borderColor: C.border }}
                  aria-label="Unterricht und Anwesenheit"
                  onClick={() => setActionEntry(e)}
                >
                  <LessonStatusCircle
                    source={lessonCircleSource(e)}
                    testId={`mobile-unterricht-status-${e.course_id}`}
                  />
                </button>
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-bold leading-snug break-words"
                    style={{ color: C.text }}
                    data-testid={`mobile-unterricht-time-${e.course_id}`}
                  >
                    {formatTime(e.start)}–{formatTime(e.end)} Uhr
                    {e.is_online ? ' · Online' : ''}
                  </p>
                  {lessonStatusSuffix(e) && (
                    <p className="text-[11px] font-semibold leading-snug" style={{ color: C.dim }}>
                      {lessonStatusSuffix(e).replace(/^ · /, '')}
                    </p>
                  )}
                  {isGroupLesson(e) ? (
                    <>
                      <p
                        className="text-base font-semibold leading-snug break-words mt-0.5"
                        data-testid={`mobile-unterricht-student-${e.course_id}`}
                      >
                        {formatClocks(e.name) || e.name}
                      </p>
                      <p
                        className="text-xs leading-snug break-words mt-0.5"
                        style={{ color: C.dim }}
                        data-testid={`mobile-unterricht-group-${e.course_id}`}
                      >
                        {groupAttendanceSummary(e.students)}
                      </p>
                      {roomLine(e) && (
                        <p
                          className="text-xs leading-snug break-words mt-0.5"
                          style={{ color: C.dim }}
                          data-testid={`mobile-unterricht-detail-${e.course_id}`}
                        >
                          {roomLine(e)}
                        </p>
                      )}
                      <button
                        type="button"
                        className="mt-1 flex items-center gap-1 text-xs font-semibold"
                        style={{ color: C.red }}
                        onClick={() => toggleGroup(e)}
                        data-testid={`mobile-unterricht-group-toggle-${e.course_id}`}
                      >
                        <ChevronDown className={`w-3.5 h-3.5 ${openGroups[groupKey(e)] ? 'rotate-180' : ''}`} />
                        {openGroups[groupKey(e)] ? 'Gruppe schließen' : 'Gruppe öffnen'}
                      </button>
                      {openGroups[groupKey(e)] && tapOptions.length > 0 && (
                        <div className="mt-2" data-testid={`mobile-unterricht-tap-${e.course_id}`}>
                          <p className="text-[11px] font-semibold" style={{ color: C.dim }}>Eingabemodus</p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {tapOptions.map((opt) => {
                              const on = tapModes[groupKey(e)] === opt.code;
                              return (
                                <button
                                  key={opt.code}
                                  type="button"
                                  aria-pressed={on}
                                  data-testid={`mobile-unterricht-tap-${e.course_id}-${opt.code}`}
                                  className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                                  style={{
                                    background: on ? C.danger : C.card,
                                    color: on ? '#fff' : C.text,
                                    border: `1px solid ${on ? C.danger : C.border}`,
                                  }}
                                  onClick={() => setTapModes((prev) => ({
                                    ...prev,
                                    [groupKey(e)]: prev[groupKey(e)] === opt.code ? null : opt.code,
                                  }))}
                                >
                                  {tapModeLabel(opt)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {openGroups[groupKey(e)] && (e.students || []).map((s) => (
                        <div
                          key={s.id}
                          className="mt-1 flex w-full items-center gap-1 rounded-lg px-2 py-1.5"
                          style={{ background: C.card }}
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                            onClick={() => {
                              setActiveStudentKey(studentActiveKey(e, s.id));
                              if (s.is_finalized || e.is_finalized) {
                                toast.error('Finalisiert — nicht mehr per Tipp änderbar.');
                                return;
                              }
                              const mode = tapOptions.find((opt) => opt.code === tapModes[groupKey(e)]);
                              if (mode) {
                                markAttendance(e, s.id, mode.teacher_status || mode.status, mode.code);
                                return;
                              }
                              setAttEntry({ ...e, students: [s] });
                            }}
                            data-testid={`mobile-unterricht-member-${e.course_id}-${s.id}`}
                          >
                            <LessonStatusCircle
                              source={{
                                category_code: s.category_code,
                                status: s.status,
                                erteilt: s.erteilt,
                                lesson_date: e.date,
                              }}
                              testId={`mobile-unterricht-member-status-${e.course_id}-${s.id}`}
                            />
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-medium break-words">{s.name}</span>
                              {birthLine(s) ? (
                                <span className="block text-[11px] break-words" style={{ color: C.dim }}>{birthLine(s)}</span>
                              ) : null}
                              {s.internal_note ? (
                                <span
                                  className="block text-[11px] break-words"
                                  style={{ color: C.dim }}
                                  data-testid={`mobile-unterricht-note-${e.course_id}-${s.id}`}
                                >
                                  {s.internal_note}
                                </span>
                              ) : null}
                              {s.notes ? (
                                <span className="block text-[11px] break-words" style={{ color: C.dim }}>{s.notes}</span>
                              ) : null}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="shrink-0 rounded-lg p-1.5"
                            style={{ color: activeStudentKey === studentActiveKey(e, s.id) ? C.red : C.dim }}
                            aria-label={`Nachricht an ${s.name}`}
                            data-testid={`mobile-unterricht-member-msg-${e.course_id}-${s.id}`}
                            onClick={() => {
                              setActiveStudentKey(studentActiveKey(e, s.id));
                              setMessageAudience('both');
                              setMessageDelivery('group');
                              setMessageEntry({ ...e, students: [s], personOnly: true });
                            }}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {studentLine(e) && (
                        <p
                          className="text-base font-semibold leading-snug break-words mt-0.5"
                          data-testid={`mobile-unterricht-student-${e.course_id}`}
                        >
                          {studentLine(e)}
                        </p>
                      )}
                      {(e.students || [])[0]?.internal_note ? (
                        <p
                          className="text-[11px] leading-snug break-words"
                          style={{ color: C.dim }}
                          data-testid={`mobile-unterricht-note-${e.course_id}`}
                        >
                          {(e.students || [])[0].internal_note}
                        </p>
                      ) : null}
                      {birthLine((e.students || [])[0]) ? (
                        <p className="text-[11px] leading-snug break-words" style={{ color: C.dim }}>
                          {birthLine((e.students || [])[0])}
                        </p>
                      ) : null}
                      <p
                        className="text-xs leading-snug break-words mt-0.5"
                        style={{ color: C.dim }}
                        data-testid={`mobile-unterricht-detail-${e.course_id}`}
                      >
                        {e.name ? <span className="block">{formatClocks(e.name) || e.name}</span> : null}
                        {roomLine(e) ? <span className="block">{roomLine(e)}</span> : null}
                      </p>
                    </>
                  )}
                  {relocationLines(e).map((line) => (
                    <p
                      key={line}
                      className="text-[11px] font-semibold mt-0.5"
                      style={{ color: C.red }}
                      data-testid={`mobile-unterricht-reloc-${e.course_id}-${e.date}`}
                    >
                      {line}
                    </p>
                  ))}
                </div>
                <button type="button" className="p-1.5 rounded-lg"
                        style={{
                          color: !isGroupLesson(e) && activeStudentKey === studentActiveKey(e, (e.students || [])[0]?.id)
                            ? C.red : C.dim,
                        }}
                        onClick={() => {
                          if (!isGroupLesson(e)) setActiveStudentKey(studentActiveKey(e, (e.students || [])[0]?.id));
                          setMessageAudience('both');
                          setMessageDelivery('group');
                          setMessageEntry(e);
                        }}
                        data-testid={`mobile-unterricht-msg-${e.course_id}`}
                        aria-label="Nachricht">
                  <MessageSquare className="w-6 h-6" />
                </button>
                <button type="button" className="p-1.5 rounded-lg self-start" style={{ color: C.text }}
                        onClick={() => setMenuEntry(e)}
                        aria-label="E-Mail, Telefon, Bemerkung"
                        data-testid={`mobile-unterricht-action-${e.course_id}`}>
                  <MoreVertical className="w-6 h-6" />
                </button>
              </div>
            </div>
            ))}
            </div>
          ))}
            {visible.length > 0 && (
              <button
                type="button"
                onClick={finalizeVisible}
                data-testid="att-bulk-finalize"
                className="w-full py-3 text-sm font-bold tracking-wide"
                style={{ color: C.red }}
              >
                EINTRÄGE FINALISIEREN
              </button>
            )}
        </div>
      ) : (
        <div className="flex-1 overflow-auto px-2 pt-3" data-testid="mobile-unterricht-calendar"
             style={{ paddingBottom: '4.5rem' }}>
          <p className="text-[11px] font-semibold px-2 mb-2" style={{ color: C.dim }}>{dayLabel}</p>
          <div className="relative mx-1 rounded-xl overflow-hidden"
               style={{
                 height: (calEndHour - calStartHour) * 60 * pxPerMin,
                 background: C.card,
                 border: `1px solid ${C.border}`,
               }}>
            {Array.from({ length: calEndHour - calStartHour + 1 }, (_, i) => calStartHour + i).map((h) => (
              <div key={h} className="absolute left-0 right-0 border-t text-[10px] pl-1"
                   style={{ top: (h - calStartHour) * 60 * pxPerMin, borderColor: C.border, color: C.dim }}>
                {h} Uhr
              </div>
            ))}
            {visible.filter((e) => !e.cancelled).map((e, idx) => {
              const [sh, sm] = String(e.start).split(':').map(Number);
              const [eh, em] = String(e.end).split(':').map(Number);
              const top = ((sh * 60 + sm) - calStartHour * 60) * pxPerMin;
              const height = Math.max(52, ((eh * 60 + em) - (sh * 60 + sm)) * pxPerMin - 2);
              return (
                <button
                  key={`${e.course_id}-${idx}`}
                  type="button"
                  onClick={() => setActionEntry(e)}
                  className="absolute left-12 right-2 rounded-md px-1.5 py-1 text-left text-[11px] text-white overflow-hidden"
                  style={{ top, height, background: C.red }}
                  data-testid={`mobile-unterricht-cal-${e.course_id}`}
                >
                  <span className="font-semibold block leading-tight break-words">
                    {formatTime(e.start)}–{formatTime(e.end)} Uhr
                  </span>
                  <span className="block leading-tight break-words">
                    {isGroupLesson(e) ? (formatClocks(e.name) || e.name || groupAttendanceSummary(e.students)) : (studentLine(e) || formatClocks(e.name) || e.name)}
                  </span>
                  {isGroupLesson(e) ? (
                    <span className="block leading-tight break-words opacity-90">{groupAttendanceSummary(e.students)}</span>
                  ) : studentLine(e) && (
                    <span className="block leading-tight break-words opacity-90">{formatClocks(e.name) || e.name}</span>
                  )}
                  {e.relocated_from_date && (
                    <span className="block truncate opacity-90">{relocationLines(e)[0]}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!(actionEntry || menuEntry || notePicker || noteTarget || contactEntry || contactDetail
        || cancelEntry || attEntry || messageEntry || moveOnce || moveSeries || areaOpen) && (
        <UnterrichtViewDock view={view} onChange={setView} />
      )}

      {actionEntry && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setActionEntry(null)} data-testid="mobile-unterricht-action-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-1" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-1 mb-2">
              <p className="text-sm font-bold">Aktion wählen</p>
              <button type="button" onClick={() => setActionEntry(null)} aria-label="Schließen"><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            <ActionRow icon={CheckCircle2} label="Erteilt" onClick={() => runTaught(actionEntry, false)} dimmed={!!actionEntry.is_finalized} testId="mobile-unterricht-act-erteilt" />
            <ActionRow icon={Video} label="Online-Unterricht vormerken" onClick={() => runOnline(actionEntry, true)} dimmed={!!actionEntry.is_finalized} />
            <ActionRow icon={VideoOff} label="Online-Unterricht entfernen" onClick={() => runOnline(actionEntry, false)} dimmed={!actionEntry.is_online || !!actionEntry.is_finalized} />
            <ActionRow icon={CheckCircle2} label="Erteilt Online" onClick={() => runTaught(actionEntry, true)} dimmed={!!actionEntry.is_finalized} testId="mobile-unterricht-act-erteilt-online" />
            <ActionRow icon={XCircle} label="EA" onClick={() => applyDirect(actionEntry, 'ea')} dimmed={!!actionEntry.is_finalized} testId="mobile-unterricht-act-ea" />
            <ActionRow icon={XCircle} label="UA" onClick={() => applyDirect(actionEntry, 'ua')} dimmed={!!actionEntry.is_finalized} testId="mobile-unterricht-act-ua" />
            <ActionRow icon={XCircle} label="LA" onClick={() => applyDirect(actionEntry, 'la')} dimmed={!!actionEntry.is_finalized} testId="mobile-unterricht-act-la" />
            <ActionRow
              icon={ArrowRightLeft}
              label={actionEntry.makeup_open && !actionEntry.relocated_to_date
                ? 'Nachholtermin festlegen…'
                : 'Termin einmalig verlegen…'}
              onClick={() => {
                openMoveOnce(actionEntry);
                setActionEntry(null);
              }}
            />
            <ActionRow
              icon={CalendarClock}
              label="Terminserie dauerhaft verlegen / ab diesem Datum ändern"
              onClick={() => {
                openMoveSeries(actionEntry);
                setActionEntry(null);
              }}
            />
            <ActionRow
              icon={Lock}
              label="Finalisieren"
              onClick={() => finalizeEntry(actionEntry)}
              dimmed={!actionEntry.is_captured || !!actionEntry.is_finalized}
              testId={`mobile-unterricht-finalize-${actionEntry.course_id}`}
            />
            <ActionRow icon={Trash2} label="Eingaben löschen" onClick={() => clearAttendance(actionEntry)} dimmed={!!actionEntry.is_finalized} />
            <button type="button" className="w-full py-3 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setActionEntry(null)}>SCHLIESSEN</button>
          </div>
        </MobileSheet>
      )}

      {menuEntry && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setMenuEntry(null)} data-testid="mobile-unterricht-menu-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-3 max-h-[85vh] overflow-y-auto" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-1">
              <p className="text-sm font-bold">Kontaktassistent</p>
              <button type="button" onClick={() => setMenuEntry(null)} aria-label="Schließen"><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            <MenuContacts people={contactsForLesson(contacts, menuEntry)} />
            <button
              type="button"
              onClick={() => openNote(menuEntry)}
              data-testid="mobile-unterricht-menu-note"
              className="w-full text-left rounded-xl px-3 py-3 text-sm font-semibold"
              style={{ background: C.bg, border: `1px solid ${C.border}` }}
            >
              {(menuEntry.students || []).some((s) => s.internal_note) ? 'Bemerkung bearbeiten' : 'Bemerkung hinzufügen'}
            </button>
            <button type="button" className="w-full py-3 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setMenuEntry(null)}>SCHLIESSEN</button>
          </div>
        </MobileSheet>
      )}

      {notePicker && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setNotePicker(null)}>
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <p className="text-sm font-bold px-1">Bemerkung für</p>
            {(notePicker.students || []).map((s) => (
              <button
                key={s.id}
                type="button"
                className="w-full text-left rounded-xl px-3 py-3 text-sm font-semibold"
                style={{ background: C.bg, border: `1px solid ${C.border}` }}
                onClick={() => beginNote(notePicker, s)}
              >
                {s.name}
              </button>
            ))}
          </div>
        </MobileSheet>
      )}

      {noteTarget && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setNoteTarget(null)} data-testid="mobile-unterricht-note-editor">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-3" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <p className="text-sm font-bold">Bemerkung · {noteTarget.student?.name}</p>
            <textarea
              value={noteDraft}
              onChange={(ev) => setNoteDraft(ev.target.value)}
              rows={4}
              className="w-full rounded-xl px-3 py-2 text-sm"
              style={{ border: `1px solid ${C.border}`, color: C.text }}
              data-testid="mobile-unterricht-note-input"
            />
            <button type="button" onClick={saveNote}
                    className="w-full rounded-xl py-3 text-sm font-bold text-white" style={{ background: C.red }}
                    data-testid="mobile-unterricht-note-save">Speichern</button>
            {noteTarget.student?.internal_note ? (
              <button type="button" className="w-full py-2 text-sm font-semibold" style={{ color: C.red }}
                      onClick={() => { setNoteDraft(''); }}>
                Text leeren
              </button>
            ) : null}
            <button type="button" className="w-full py-2 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setNoteTarget(null)}>Abbrechen</button>
          </div>
        </MobileSheet>
      )}

      {contactEntry && !contactDetail && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setContactEntry(null)} data-testid="mobile-unterricht-contact-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2 max-h-[85vh] overflow-y-auto" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-1 mb-1">
              <div>
                <p className="text-sm font-bold">Kontaktassistent</p>
                <p className="text-[11px]" style={{ color: C.dim }}>
                  {formatTime(contactEntry.start)} · {contactEntry.name}
                </p>
              </div>
              <button type="button" onClick={() => setContactEntry(null)} aria-label="Schließen"><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            {lessonContacts.length === 0 && (
              <p className="text-sm px-1 py-2" style={{ color: C.dim }} data-testid="mobile-unterricht-contact-empty">
                Keine Kontaktdaten für diesen Termin.
              </p>
            )}
            {lessonContacts.map((c) => (
              <div key={`${c.person_type}-${c.person_id}`} className="rounded-xl p-3" style={{ background: C.bg, border: `1px solid ${C.border}` }}
                   data-testid={`mobile-unterricht-person-${c.person_type}-${c.person_id}`}>
                <p className="text-sm font-semibold">{formatClocks(c.name) || c.name}</p>
                <p className="text-[11px] mb-2" style={{ color: C.dim }}>
                  {c.person_type === 'guardian' ? (c.subgroup || 'Eltern') : 'Schüler:in'}
                  {c.user_id ? '' : ' · nicht in der App'}
                </p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => startChat(c)}
                          className="flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold"
                          style={{ background: C.red, color: '#fff' }}
                          data-testid={`mobile-unterricht-chat-${c.person_id}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> Nachricht
                  </button>
                  <button type="button" onClick={() => setContactDetail(c)}
                          className="flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold"
                          style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}
                          data-testid={`mobile-unterricht-open-contact-${c.person_id}`}>
                    <Phone className="w-3.5 h-3.5" /> Tel / E-Mail
                  </button>
                </div>
              </div>
            ))}
            <button type="button" className="w-full py-3 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setContactEntry(null)}>Schließen</button>
          </div>
        </MobileSheet>
      )}

      {contactDetail && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setContactDetail(null)} data-testid="mobile-unterricht-contact-detail">
          <div className="w-full max-w-md rounded-t-2xl p-5" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">{contactDetail.name}</h3>
              <button type="button" onClick={() => setContactDetail(null)}><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            {splitPhoneNumbers(contactDetail.phone).map((number, index) => (
              <a key={`${number}-${index}`}
                 href={toTelHref(number) || `tel:${number}`}
                 className="flex items-center gap-2 py-2 text-sm" style={{ color: C.text }}
                 data-testid={index === 0 ? 'mobile-unterricht-call' : `mobile-unterricht-call-${index + 1}`}>
                <Phone className="w-4 h-4" style={{ color: C.red }} />{number}
              </a>
            ))}
            {contactDetail.email && (
              <a href={`mailto:${contactDetail.email}`} className="flex items-center gap-2 py-2 text-sm" style={{ color: C.text }}
                 data-testid="mobile-unterricht-mailto">
                <Mail className="w-4 h-4" style={{ color: C.red }} />{contactDetail.email}
              </a>
            )}
            {!contactDetail.phone && !contactDetail.email && (
              <p className="text-sm" style={{ color: C.dim }}>Keine Telefonnummer oder E-Mail hinterlegt.</p>
            )}
            <button type="button" onClick={() => startChat(contactDetail)}
                    className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold"
                    style={{ background: C.red, color: '#fff' }}
                    data-testid="mobile-unterricht-detail-chat">
              <MessageSquare className="w-4 h-4" /> Nachricht senden
            </button>
          </div>
        </MobileSheet>
      )}

      {cancelEntry && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setCancelEntry(null)} data-testid="mobile-unterricht-cancel-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2 max-h-[85vh] overflow-y-auto" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="text-sm font-bold">Ausfallart wählen</p>
                <p className="text-[11px]" style={{ color: C.dim }}>
                  {formatTime(cancelEntry.start)} · {cancelEntry.name}
                </p>
              </div>
              <button type="button" onClick={() => setCancelEntry(null)} aria-label="Schließen">
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            <p className="text-[11px] px-1 pb-1" style={{ color: C.dim }}>
              Die Kategorie bestimmt Abrechnung, Karteneinheit und Nachholpflicht.
            </p>
            {cancelOptions.map((cat) => (
              <button
                key={cat.code}
                type="button"
                onClick={() => applyCancelCategory(cancelEntry, cat)}
                data-testid={`mobile-unterricht-cancel-${cat.code}`}
                className="w-full text-left rounded-xl px-3 py-3 active:opacity-80"
                style={{ background: C.bg, border: `1px solid ${C.border}` }}
              >
                <span className="block text-sm font-semibold">{cat.label}</span>
                <span className="block text-[11px] mt-0.5" style={{ color: C.dim }}>{consequenceText(cat)}</span>
              </button>
            ))}
            <button type="button" className="w-full py-3 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setCancelEntry(null)}>Abbrechen</button>
          </div>
        </MobileSheet>
      )}

      {attEntry && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setAttEntry(null)} data-testid="mobile-unterricht-attendance-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-3 max-h-[85vh] overflow-y-auto" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold">Anwesenheit</p>
                <p className="text-[11px]" style={{ color: C.dim }}>{formatTime(attEntry.start)} · {attEntry.name}</p>
              </div>
              <button type="button" onClick={() => setAttEntry(null)} aria-label="Schließen"><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            {(attEntry.students || []).map((s) => (
              <div key={s.id} className="rounded-xl p-3 space-y-2" style={{ background: C.bg, border: `1px solid ${C.border}` }}
                   data-testid={`mobile-unterricht-att-student-${s.id}`}>
                <p className="text-sm font-semibold">{s.name}</p>
                <div className="grid grid-cols-1 gap-1.5">
                  {(presenceOptions.length ? presenceOptions : ATT_OPTIONS.filter((o) => o.status !== 'none')).map((opt) => (
                    <button
                      key={opt.code || opt.status}
                      type="button"
                      onClick={() => markAttendance(attEntry, s.id, opt.teacher_status || opt.status, opt.code)}
                      className="text-left rounded-lg px-3 py-2"
                      style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}
                      data-testid={`mobile-unterricht-att-${s.id}-${opt.code || opt.status}`}
                    >
                      <span className="block text-xs font-medium">{opt.label}</span>
                      {opt.code && (
                        <span className="block text-[10px] mt-0.5" style={{ color: C.dim }}>{consequenceText(opt)}</span>
                      )}
                    </button>
                  ))}
                  <button type="button" onClick={() => markAttendance(attEntry, s.id, 'none')}
                          className="text-left text-xs font-medium rounded-lg px-3 py-2"
                          style={{ background: C.card, border: `1px solid ${C.border}`, color: C.dim }}
                          data-testid={`mobile-unterricht-att-${s.id}-none`}>
                    Eintrag löschen
                  </button>
                </div>
              </div>
            ))}
            <button type="button" className="w-full py-3 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setAttEntry(null)}>SCHLIESSEN</button>
          </div>
        </MobileSheet>
      )}

      {messageEntry && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setMessageEntry(null)} data-testid="mobile-unterricht-message-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-3 max-h-[85vh] overflow-y-auto" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <p className="text-sm font-bold">Nachricht</p>
            <p className="text-xs" style={{ color: C.dim }}>
              {messageEntry.personOnly ? (studentLine(messageEntry) || messageEntry.name) : (messageEntry.name || 'Gruppe')}
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              {[
                ['students', 'Nur Schüler:innen'],
                ['guardians', 'Nur Eltern'],
                ['both', 'Schüler:innen und Eltern'],
              ].map(([value, label]) => (
                <button key={value} type="button" onClick={() => setMessageAudience(value)}
                        data-testid={`mobile-unterricht-audience-${value}`}
                        className="rounded-lg px-3 py-2 text-left text-sm font-semibold"
                        style={{
                          background: messageAudience === value ? C.rose : C.bg,
                          border: `1px solid ${C.border}`,
                        }}>
                  {label}
                </button>
              ))}
              {messageEntry.personOnly && messageTargets(contacts, messageEntry, 'guardians').length > 1
                && messageTargets(contacts, messageEntry, 'guardians').map((guardian) => (
                  <button key={guardian.person_id} type="button"
                          onClick={() => setMessageAudience(`guardian:${guardian.person_id}`)}
                          data-testid={`mobile-unterricht-audience-guardian-${guardian.person_id}`}
                          className="rounded-lg px-3 py-2 text-left text-sm font-semibold"
                          style={{
                            background: messageAudience === `guardian:${guardian.person_id}` ? C.rose : C.bg,
                            border: `1px solid ${C.border}`,
                          }}>
                    Nur {guardian.name}
                  </button>
                ))}
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              <button type="button" onClick={() => setMessageDelivery('group')}
                      data-testid="mobile-unterricht-delivery-group"
                      className="rounded-lg px-3 py-2 text-left text-sm font-semibold"
                      style={{ background: messageDelivery === 'group' ? C.rose : C.bg, border: `1px solid ${C.border}` }}>
                Gruppenchat
              </button>
              <button type="button" onClick={() => setMessageDelivery('bcc')}
                      data-testid="mobile-unterricht-delivery-bcc"
                      className="rounded-lg px-3 py-2 text-left text-sm font-semibold"
                      style={{ background: messageDelivery === 'bcc' ? C.rose : C.bg, border: `1px solid ${C.border}` }}>
                Einzeln, Empfänger sehen sich nicht
              </button>
            </div>
            <div data-testid="mobile-unterricht-message-names">
              {messageTargets(contacts, messageEntry, messageAudience).map((person) => (
                <p key={`${person.person_type}-${person.person_id}`} className="text-sm">{person.name}</p>
              ))}
              {messageTargets(contacts, messageEntry, messageAudience).length === 0 && (
                <p className="text-sm" style={{ color: C.dim }}>Niemand mit App-Konto in dieser Auswahl.</p>
              )}
            </div>
            <button type="button" onClick={sendLessonMessage}
                    className="w-full rounded-xl py-3 text-sm font-bold text-white" style={{ background: C.red }}
                    data-testid="mobile-unterricht-message-send">Nachricht öffnen</button>
            <button type="button" className="w-full py-2 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setMessageEntry(null)}>Abbrechen</button>
          </div>
        </MobileSheet>
      )}

      {moveOnce && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setMoveOnce(null)} data-testid="mobile-unterricht-move-once">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <MoveHeader
              entry={moveOnce}
              title={moveOnce.makeup_open && !moveOnce.relocated_to_date
                ? 'Nachholtermin festlegen'
                : 'Termin verlegen'}
            />
            <label className="block text-xs font-semibold" style={{ color: C.dim }}>
              Neues Datum
              <input type="date" value={moveForm.date} onChange={(e) => setMoveForm((f) => ({ ...f, date: e.target.value }))}
                     className="mt-1 w-full rounded-lg px-3 py-2" style={{ border: `1px solid ${C.border}` }}
                     data-testid="mobile-unterricht-move-date" />
            </label>
            <label className="block text-xs font-semibold" style={{ color: C.dim }}>
              Beginn
              <input type="time" value={moveForm.start}
                     onChange={(e) => {
                       const start = e.target.value;
                       setMoveForm((f) => ({
                         ...f,
                         start,
                         end: endFromStart(start, lessonDurationMinutes(moveOnce)),
                       }));
                     }}
                     className="mt-1 w-full rounded-lg px-3 py-2" style={{ border: `1px solid ${C.border}` }}
                     data-testid="mobile-unterricht-move-start" />
            </label>
            <label className="block text-xs font-semibold" style={{ color: C.dim }}>
              Ende
              <input type="time" readOnly value={moveForm.end}
                     className="mt-1 w-full rounded-lg px-3 py-2" style={{ border: `1px solid ${C.border}`, background: C.bg }}
                     data-testid="mobile-unterricht-move-end" />
            </label>
            <PlaceFields form={moveForm} setForm={setMoveForm} places={places} rooms={rooms} idPrefix="mobile-unterricht-move" />
            <button type="button" onClick={submitMoveOnce}
                    className="w-full rounded-xl py-3 text-sm font-bold text-white" style={{ background: C.red }}
                    data-testid="mobile-unterricht-move-save">Speichern</button>
            <button type="button" className="w-full py-2 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setMoveOnce(null)}>Abbrechen</button>
          </div>
        </MobileSheet>
      )}

      {moveSeries && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setMoveSeries(null)} data-testid="mobile-unterricht-move-series">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <MoveHeader entry={moveSeries} title="Terminserie dauerhaft verlegen" />
            <label className="block text-xs font-semibold" style={{ color: C.dim }}>
              Ab Datum
              <input type="date" value={seriesForm.date} onChange={(e) => setSeriesForm((f) => ({ ...f, date: e.target.value }))}
                     className="mt-1 w-full rounded-lg px-3 py-2" style={{ border: `1px solid ${C.border}` }}
                     data-testid="mobile-unterricht-series-date" />
            </label>
            <label className="block text-xs font-semibold" style={{ color: C.dim }}>
              Wochentag
              <select value={seriesForm.dow} onChange={(e) => setSeriesForm((f) => ({ ...f, dow: Number(e.target.value) }))}
                      className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{ border: `1px solid ${C.border}` }}
                      data-testid="mobile-unterricht-series-dow">
                {DAY_LABELS.map((lab, i) => <option key={lab} value={i}>{lab}</option>)}
              </select>
            </label>
            <label className="block text-xs font-semibold" style={{ color: C.dim }}>
              Startzeit
              <input type="time" value={seriesForm.start} onChange={(e) => setSeriesForm((f) => ({ ...f, start: e.target.value }))}
                     className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{ border: `1px solid ${C.border}` }}
                     data-testid="mobile-unterricht-series-start" />
            </label>
            <p className="text-xs" style={{ color: C.dim }} data-testid="mobile-unterricht-series-end">
              Ende {endFromStart(seriesForm.start, lessonDurationMinutes(moveSeries))} Uhr
            </p>
            <PlaceFields form={seriesForm} setForm={setSeriesForm} places={places} rooms={rooms} idPrefix="mobile-unterricht-series" />
            <button type="button" onClick={submitMoveSeries}
                    className="w-full rounded-xl py-3 text-sm font-bold text-white" style={{ background: C.red }}
                    data-testid="mobile-unterricht-series-save">Speichern</button>
            <button type="button" className="w-full py-2 text-sm font-semibold" style={{ color: C.dim }}
                    onClick={() => setMoveSeries(null)}>Abbrechen</button>
          </div>
        </MobileSheet>
      )}

      {areaOpen && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setAreaOpen(false)} data-testid="mobile-unterricht-area-picker">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2" style={{ background: C.card }}
               onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-bold px-1">Ansicht wählen</p>
            {areas.map((a) => (
              <button key={a} type="button"
                      onClick={() => { switchArea(a); setAreaOpen(false); if (!isStaffArea(a)) nav('/m'); }}
                      className="w-full text-left rounded-xl px-3 py-3 text-sm font-semibold"
                      style={{ background: a === area ? C.rose : C.bg, border: `1px solid ${C.border}` }}
                      data-testid={`mobile-unterricht-area-${a}`}>
                {AREA_LABEL[a] || a}
              </button>
            ))}
          </div>
        </MobileSheet>
      )}
    </MobileShell>
  );
}

function LessonStatusCircle({ source, testId }) {
  const resolved = resolveAttendanceStatus({ today: todayIsoLocal(), ...source });
  const empty = !resolved || (resolved.code === 'offen' && !source.makeup_open);
  const label = empty
    ? ''
    : (resolved.code === 'erteilt_online' || (resolved.code === 'erteilt' && source.is_online))
      ? 'Online'
      : resolved.short;
  const filled = label === '✓';
  return (
    <span
      data-testid={testId}
      title={resolved?.title || 'Offen'}
      aria-label={resolved?.title || 'Offen'}
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 font-bold leading-none"
      style={{
        borderColor: label ? C.red : C.border,
        background: filled ? C.red : (label ? C.rose : '#fff'),
        color: filled ? '#fff' : C.red,
        fontSize: label === 'Online' ? 9 : 13,
      }}
    >
      {label}
    </span>
  );
}

function MenuContacts({ people }) {
  const emails = (people || []).filter((c) => c.email);
  const phones = (people || []).flatMap((c) => splitPhoneNumbers(c.phone).map((number) => ({
    number, name: c.name,
  })));
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold px-1">E-Mail senden</p>
        {emails.length === 0 && (
          <p className="text-xs px-1 py-1" style={{ color: C.dim }}>Keine E-Mail hinterlegt.</p>
        )}
        {emails.map((c) => (
          <a key={`mail-${c.person_id}`} href={`mailto:${c.email}`}
             className="block rounded-xl px-3 py-2 text-sm" style={{ color: C.text }}>
            {c.name}
          </a>
        ))}
      </div>
      <div>
        <p className="text-sm font-semibold px-1">Telefonieren</p>
        {phones.length === 0 && (
          <p className="text-xs px-1 py-1" style={{ color: C.dim }}>Keine Telefonnummer hinterlegt.</p>
        )}
        {phones.map((c) => (
          <a key={`tel-${c.number}`} href={toTelHref(c.number) || `tel:${c.number}`}
             className="block rounded-xl px-3 py-2 text-sm" style={{ color: C.text }}>
            {c.name} · {c.number}
          </a>
        ))}
      </div>
    </div>
  );
}

function ActionRow({ icon: Icon, label, onClick, dimmed, testId }) {
  return (
    <button
      type="button"
      disabled={dimmed}
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left disabled:opacity-40"
      style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
    >
      <Icon className="w-5 h-5" style={{ color: C.red }} />
      <span className="text-sm font-medium flex-1">{label}</span>
      <ChevronRight className="w-4 h-4" style={{ color: C.dim }} />
    </button>
  );
}
